import { isLaunchDateInPast } from '../components/Containers/PurchaseForm/utils';
import type { ProductStatus } from '../types/Analytics';
import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
  PurchasingState,
} from '../types/Purchasing';
import { storyblokProductReleaseType } from '../utils/storyblokReleaseType';

export function purchasingState({
  available,
  closingDate,
  releaseType,
  tags,
}: {
  releaseType: string | StoryblokReleaseType;
  closingDate?: string;
  available?: boolean;
  tags?: string[];
}): PurchasingState {
  const release = storyblokProductReleaseType({ releaseType });

  // If the release is a draw, and the draw has closed, mark as sold out
  if (release === 'draw' && closingDate && isLaunchDateInPast(closingDate))
    return 'SOLD_OUT';

  if (!tags) return 'UNKNOWN';

  // If the Shopify "Sold out" tag is present, mark as sold out
  if (tags?.includes('Sold out')) return 'SOLD_OUT';

  if (release === 'private') return 'ENQUIRY';

  if (tags?.includes('Coming soon')) return 'COMING_SOON';

  if (!available) return 'UNAVAILABLE';

  return 'AVAILABLE';
}

// What a shopper is shown: a work Shopify won't sell, such as a code-only
// one, reads as sold out and offers the waitlist.
export const presentedPurchasingState = (
  state: PurchasingState,
): PurchasingState => (state === 'UNAVAILABLE' ? 'SOLD_OUT' : state);

export const productStatusFor = (state: PurchasingState): ProductStatus => {
  if (state === 'COMING_SOON') return 'coming soon';
  if (state === 'ENQUIRY') return 'enquiry';
  if (state === 'SOLD_OUT') return 'sold out';
  if (state === 'UNAVAILABLE') return 'unavailable';

  return 'available';
};

export const getProductStatus = (
  product: ProductType,
  purchasingController: ProductPurchasingController<any> | undefined,
): ProductStatus => {
  if (!purchasingController?.product) return 'unavailable';

  return productStatusFor(
    purchasingState({
      releaseType: product.releaseType,
      closingDate: product.closingDate,
      available: purchasingController?.product?.available,
      tags: purchasingController?.product?.tags,
    }),
  );
};

export const getProductTileStatus = (product: Product) => {
  if (product.availability === 'Coming soon') return 'coming soon';
  if (product.releaseType === 'private') return 'enquiry';
  if (product.availability === 'Sold out') return 'sold out';
  return 'available';
};

export const getProductRef = (
  purchasingController: ProductPurchasingController<ProductPurchaseDetails>,
): string => skuRef(purchasingController?.product?.sku);

export const skuRef = (sku: string | undefined): string => {
  if (!sku) return '';
  return sku.substring(0, 14);
};
